/**
 * Input Validation Utilities
 * Provides validation functions for form inputs across the application
 */

// Characters to prevent in text inputs: - < > / ' " ( ) = ` % & ; : # \ + - { } [ ] | @ $ * , . _ ~
const RESTRICTED_CHARS = /[<>\/'"()=`%&;:#\\+\{}\[\]|@$*_~]/g;

// Characters to prevent in search inputs (allows: @ . - _ ( ) )
// Prevents: < > / ' " = ` % & ; : # \ + { } [ ] | $ * , ~
const RESTRICTED_SEARCH_CHARS = /[<>\/'"=`%&;:#\\+{}\[\]|$*,~]/g;

/**
 * Sanitizes text input by removing restricted characters
 * @param {string} value - Input value
 * @returns {string} - Sanitized value
 */
export const sanitizeTextInput = (value) => {
  if (typeof value !== 'string') return value;
  return value.replace(RESTRICTED_CHARS, '');
};

/**
 * Sanitizes search input by removing restricted characters
 * Allows email characters: @ . - _
 * @param {string} value - Input value
 * @returns {string} - Sanitized value
 */
export const sanitizeSearchInput = (value) => {
  if (typeof value !== 'string') return value;
  return value.replace(RESTRICTED_SEARCH_CHARS, '');
};

/**
 * Sanitizes email input by removing only dangerous characters
 * Allows email characters: @ . - _ + (plus sign is valid in email addresses)
 * Prevents: < > / ' " ( ) = ` % & ; : # \ { } [ ] | $ * , ~
 * @param {string} value - Input value
 * @returns {string} - Sanitized value
 */
export const sanitizeEmailInput = (value) => {
  if (typeof value !== 'string') return value;
  // Remove only dangerous HTML and script injection characters
  // Allow: @ . - _ + (all valid email characters)
  // Note: + is NOT in the regex, so it's allowed
  return value.replace(/[<>\/'"()`%&;:#\\{}\[\]|$*,~]/g, '');
};

/**
 * Sanitizes address input by removing restricted characters
 * Allows address characters: ( ) - , .
 * Prevents: < > / ' " = ` % & ; : # \ + { } [ ] | @ $ * ~
 * @param {string} value - Input value
 * @returns {string} - Sanitized value
 */
export const sanitizeAddressInput = (value) => {
  if (typeof value !== 'string') return value;
  // Allow: letters, numbers, spaces, ( ) - , .
  // Remove: < > / ' " = ` % & ; : # \ + { } [ ] | @ $ * ~
  return value.replace(/[<>\/'"()`%&;:#\\+{}\[\]|@$*~]/g, '');
};

/**
 * Sanitizes description/notes input by removing only dangerous characters
 * Allows common punctuation: , . ! ? : ; - ( ) [ ] { } " ' / \ @ # $ % & * + = | ~ ` _
 * Prevents only: < > (to prevent HTML injection)
 * @param {string} value - Input value
 * @returns {string} - Sanitized value
 */
export const sanitizeDescriptionInput = (value) => {
  if (typeof value !== 'string') return value;
  // Only remove < and > to prevent HTML injection, allow all other characters
  return value.replace(/[<>]/g, '');
};

/**
 * Validates and sanitizes number input - only positive numbers (0 and greater)
 * @param {string|number} value - Input value
 * @param {number} maxValue - Maximum value allowed
 * @param {number} minValue - Minimum value allowed (default: 0)
 * @returns {object} - { value: string|number, isValid: boolean, error: string }
 */
export const validateNumberInput = (value, maxValue = Infinity, minValue = 0) => {
  // Allow empty string for optional fields
  if (value === '' || value === null || value === undefined) {
    return { value: '', isValid: true, error: '' };
  }

  // Convert to number
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  // Check if valid number
  if (isNaN(numValue)) {
    return { value: '', isValid: false, error: 'Please enter a valid number' };
  }

  // Check if negative
  if (numValue < 0) {
    return { value: minValue, isValid: false, error: 'Only positive numbers are allowed' };
  }

  // Check minimum
  if (numValue < minValue) {
    return { value: minValue, isValid: false, error: `Value must be ${minValue} or greater` };
  }

  // Check maximum
  if (numValue > maxValue) {
    return { value: maxValue, isValid: false, error: `Value must be ${maxValue} or less` };
  }

  return { value: numValue, isValid: true, error: '' };
};

/**
 * Validates phone number - allows up to 15 digits
 * @param {string} value - Input value
 * @returns {object} - { value: string, isValid: boolean, error: string }
 */
export const validatePhoneInput = (value) => {
  if (typeof value !== 'string') return { value: '', isValid: false, error: 'Invalid input' };

  // Allow digits and '+'
  // Remove all characters except digits and '+'
  let sanitized = value.replace(/[^\d+]/g, '');

  // If '+' is present, ensure it's only at the beginning
  if (sanitized.includes('+')) {
    const hasPlusAtStart = sanitized.startsWith('+');
    sanitized = (hasPlusAtStart ? '+' : '') + sanitized.replace(/\+/g, '');
  }

  // Limit to 15 characters (including '+' if present)
  // FIELD_LIMITS.phone is 15
  const limitedValue = sanitized.slice(0, 15);

  if (limitedValue.length === 0) {
    return { value: '', isValid: true, error: '' }; // Allow empty for optional fields
  }

  // Valid if at least 10 digits/characters
  if (limitedValue.length < 10) {
    return { value: limitedValue, isValid: false, error: 'Phone number must be at least 10 digits' };
  }

  return { value: limitedValue, isValid: true, error: '' };
};

/**
 * Validates email input
 * @param {string} value - Input value
 * @returns {object} - { value: string, isValid: boolean, error: string }
 */
export const validateEmailInput = (value) => {
  if (!value) return { value: '', isValid: true, error: '' };

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(value)) {
    return { value, isValid: false, error: 'Please enter a valid email address' };
  }

  return { value, isValid: true, error: '' };
};

/**
 * Handles text input change with sanitization
 * @param {Event} e - Input change event
 * @param {Function} setValue - State setter function (optional)
 * @param {number} maxLength - Maximum character limit
 * @returns {string} - Sanitized value
 */
export const handleTextInputChange = (e, setValue = null, maxLength = 100) => {
  let value = e.target.value;

  // Remove restricted characters
  value = sanitizeTextInput(value);

  // Enforce max length
  value = value.slice(0, maxLength);

  if (setValue) {
    setValue(value);
  }
  return value;
};

/**
 * Handles search input change with sanitization
 * @param {Event} e - Input change event
 * @param {Function} setValue - State setter function
 * @param {number} maxLength - Maximum character limit (default: 150)
 * @returns {string} - Sanitized value
 */
export const handleSearchInputChange = (e, setValue, maxLength = 150) => {
  let value = e.target.value;

  // Remove restricted characters
  value = sanitizeSearchInput(value);

  // Enforce max length
  value = value.slice(0, maxLength);

  if (setValue) {
    setValue(value);
  }
  return value;
};

/**
 * Handles number input change with validation
 * @param {Event} e - Input change event
 * @param {Function} setValue - State setter function (optional)
 * @param {number} maxValue - Maximum value allowed
 * @param {number} minValue - Minimum value allowed (default: 0)
 * @returns {number|string} - Validated value
 */
export const handleNumberInputChange = (e, setValue = null, maxValue = Infinity, minValue = 0) => {
  let value = e.target.value;

  // Allow empty string
  if (value === '') {
    if (setValue) setValue('');
    return '';
  }

  // Remove all non-digit characters (except decimal point for duration)
  // For duration in minutes, we'll allow integers only
  const digitsOnly = value.replace(/\D/g, '');

  if (digitsOnly === '') {
    if (setValue) setValue('');
    return '';
  }

  const numValue = parseInt(digitsOnly, 10);

  // Check if valid number
  if (isNaN(numValue)) {
    if (setValue) setValue('');
    return '';
  }

  // Ensure non-negative
  if (numValue < 0) {
    if (setValue) setValue(minValue);
    return minValue;
  }

  // Enforce maximum
  if (numValue > maxValue) {
    if (setValue) setValue(maxValue);
    return maxValue;
  }

  if (setValue) setValue(numValue);
  return numValue;
};

/**
 * Handles phone number input change with validation
 * @param {Event} e - Input change event
 * @param {Function} setValue - State setter function
 * @returns {string} - Validated phone number
 */
export const handlePhoneInputChange = (e, setValue) => {
  let value = e.target.value;

  // Allow digits and '+'
  let sanitized = value.replace(/[^\d+]/g, '');

  // If '+' is present, ensure it's only at the beginning
  if (sanitized.includes('+')) {
    const hasPlusAtStart = sanitized.startsWith('+');
    sanitized = (hasPlusAtStart ? '+' : '') + sanitized.replace(/\+/g, '');
  }

  // Limit to 15 characters
  const limitedValue = sanitized.slice(0, 15);

  setValue(limitedValue);
  return limitedValue;
};

/**
 * Validates text input - checks length and required
 * @param {string} value - Input value
 * @param {number} maxLength - Maximum character limit
 * @param {number} minLength - Minimum character limit (default: 0)
 * @returns {object} - { value: string, isValid: boolean, error: string }
 */
export const validateText = (value, maxLength = 100, minLength = 0) => {
  // Allow empty string for optional fields
  if (value === '' || value === null || value === undefined) {
    return { value: '', isValid: true, error: '' };
  }

  const strValue = String(value);
  const length = strValue.length;

  // Check minimum length
  if (minLength > 0 && length < minLength) {
    return { value: strValue, isValid: false, error: `Text must be at least ${minLength} characters` };
  }

  // Check maximum length
  if (length > maxLength) {
    return { value: strValue.slice(0, maxLength), isValid: false, error: `Text must be ${maxLength} characters or less` };
  }

  return { value: strValue, isValid: true, error: '' };
};

/**
 * Gets character count info for display
 * @param {string} value - Input value
 * @param {number} maxLength - Maximum character limit
 * @returns {string} - Character count string like "12/100"
 */
export const getCharacterCount = (value, maxLength) => {
  const current = value ? value.length : 0;
  return `${current}/${maxLength}`;
};

/**
 * Validates required fields in a form
 * @param {object} formData - Form data object
 * @param {array} requiredFields - Array of required field names
 * @returns {object} - { isValid: boolean, errors: object }
 */
export const validateRequiredFields = (formData, requiredFields) => {
  const errors = {};
  let isValid = true;

  requiredFields.forEach(field => {
    const value = formData[field];
    if (!value || (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray && Array.isArray(value) && value.length === 0)) {
      errors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
      isValid = false;
    }
  });

  return { isValid, errors };
};

/**
 * Field-specific character limits
 */
export const FIELD_LIMITS = {
  search: 150,
  title: 255,
  customerName: 100,
  address: 100,
  notes: 1000,
  phone: 15,
  mobile: 15,
  firstName: 50,
  lastName: 50,
  duration: 999, // Maximum duration in minutes (reasonable limit)
  year: 9999, // 4 digits for year
  productName: 200,
  sku: 50,
  batch: 50,
  company: 100,
  city: 50,
  state: 50,
  pincode: 6,
  gstin: 15,
  orderNumber: 50,
  quoteTitle: 200,
  validity: 999, // days
  terms: 2000,
  message: 1000,
  versionNotes: 500,
  opportunityName: 200,
  opportunityDescription: 1000,
  gstNumber: 15,
  drugLicenseNumber: 30,
  productRequirement: 500,
  panNumber: 10
};

/**
 * Field-specific number limits
 */
export const NUMBER_LIMITS = {
  duration: { min: 0, max: 999 },
  year: { min: 0, max: 9999 },
  validity: { min: 1, max: 365 }, // days
  quantity: { min: 1, max: 99999 },
  unitPrice: { min: 0, max: 99999999 },
  discount: { min: 0, max: 100 }, // percentage
  tax: { min: 0, max: 100 }, // percentage
  pincode: { min: 0, max: 999999 },
  annualRevenue: { min: 0, max: 999999999999 }, // 12 digits max for revenue
  leadValue: { min: 0, max: 999999999999 }, // 12 digits max for lead value
  budget: { min: 0, max: 999999999999 }, // 12 digits max for budget
  opportunityValue: { min: 0, max: 999999999999 }, // 12 digits max for opportunity value
  tenderValue: { min: 0, max: 999999999999 } // 12 digits max for tender value
};
